/**
 * Derive a human-readable, hierarchical label from a Next.js pathname.
 *
 * Used by the shell's /api/favorites route to self-heal generic labels at
 * read time, giving every saved page a clean, specific label like
 * "CRM · Leads · #123" instead of falling back to the site name from
 * `document.title`. Pure function — no React, no DOM — so it imports
 * cleanly into route handlers without bundle warnings.
 *
 * Strategy: walk the path segment-by-segment, look up known products and
 * common sub-routes in dictionaries, prettify unknown segments, shorten
 * id-shaped segments. The breadcrumb chain (joined by " · ") doubles as a
 * scent trail for navigation.
 */

// RO-3-02 / RO-4-04: NOT the bare `@excellent/core` barrel — that chain
// (index → objectives → events/engine → db → security/{keystore,keychain})
// ships the SQLCipher key path to a client bundle for the sake of one string
// constant. `jtbd-registry.ts` has zero imports of its own, so it gets an
// additive subpath export instead (mirrors the `goal-health` fix alongside
// it). This module's own header comment already promises "imports cleanly
// into route handlers without bundle warnings" — the bare import silently
// broke that promise the moment a client route (not just a route HANDLER)
// reached it; discovered via `pnpm build:client` failing on a Node-builtin
// import three modules further down the same chain.
import { HIRING_DISPLAY_NAME } from "@excellent/core/jtbd-registry";

const PRODUCT_LABELS: Record<string, string> = {
  // Installed products
  lens: "Analytics",
  hiring: HIRING_DISPLAY_NAME,
  crm: "CRM",
  // Built-in shell sections
  projects: "Projects",
  board: "Board",
  planner: "Planner",
  tasks: "Issues",
  goals: "Goals",
  agents: "Verification",
  products: "Products",
  settings: "Settings",
  welcome: "Welcome",
  styleguide: "Style guide",
  favorites: "Favorites",
  recent: "Recent",
};

// Per-product subpath dictionaries. Only include known sub-routes; unknowns
// fall through to prettify().
const SUBPATH_LABELS: Record<string, Record<string, string>> = {
  // Verification panel — incl. legacy/developer operations surfaces kept behind
  // verifier-first chrome. See docs/agents/ANALYTICS-EFFICIENCY-INTEGRATION-PRD.md.
  agents: {
    analytics: "Legacy analytics",
    fleet: "Legacy fleet",
    quality: "Reliability",
    you: "You",
    verification: "Verification",
    "verification-ops": "Readiness",
    workflows: "Legacy workflows",
    health: "Legacy health",
    claims: "Developer claims",
    lanes: "Developer lanes",
    triage: "Developer triage",
    qa: "Developer QA",
    runs: "Legacy runs",
    goals: "Legacy goals",
    roadmap: "Roadmap",
    sessions: "Sessions",
    costs: "Costs",
    activity: "Activity",
    tools: "Tools",
    projects: "Projects",
    memory: "Memory",
    plans: "Plans",
    todos: "Todos",
    history: "History",
    export: "Export",
    settings: "Settings",
  },
  lens: {
    overview: "Overview",
    sessions: "Sessions",
    plans: "Plans",
    projects: "Projects",
    activity: "Activity",
    history: "History",
    todos: "Todos",
    tools: "Tools",
    memory: "Memory",
    costs: "Costs",
    export: "Export",
    settings: "Settings",
  },
  hiring: {
    jobs: "Jobs",
    candidates: "Candidates",
    pipeline: "Pipeline",
    applications: "Applications",
    settings: "Settings",
  },
  crm: {
    leads: "Leads",
    opportunities: "Opportunities",
    pipeline: "Pipeline",
    sequences: "Sequences",
    reports: "Reports",
    tasks: "Issues",
    templates: "Templates",
    import: "Import",
    settings: "Settings",
  },
  // Database Explorer — the editorial leaf labels for the six §5 destinations, so
  // the breadcrumb (now the sole page title since the pages drop their own
  // PageNav H1) reads "Database › SQL Editor" rather than a prettified "Sql".
  database: {
    assistant: "Assistant",
    objects: "Objects",
    views: "Views",
    new: "New view",
    schema: "Schema",
    editor: "Table Editor",
    sql: "SQL Editor",
    migrations: "Migrations",
    sync: "Fleet Sync",
    conflicts: "Sync Conflicts",
    duplicates: "Duplicates",
  },
  settings: {
    products: "Products",
    appearance: "Appearance",
    profile: "Profile",
    notifications: "Notifications",
    shortcuts: "Shortcuts",
    workspace: "Workspace",
    // Display-only rename (route stays /settings/workspace/team), mirroring the
    // lens → "Analytics" precedent above.
    team: "Multiplayer",
    agents: "Agents",
    advanced: "Advanced",
    lens: "Analytics",
    hiring: HIRING_DISPLAY_NAME,
    crm: "CRM",
  },
};

// Project-tree section slugs (mirrors SECTIONS in section-rail.tsx) PLUS the
// five Normal-Core "stream" slugs (hand-kept mirror of STREAMS in
// packages/core/src/stream-registry.ts). Both inhabit the <section> segment of
// /projects/<p>/<seg>, so one dictionary labels both. The stream entries are
// inert: streams nav was retired in favor of modes (the NEXT_PUBLIC_STREAMS_NAV
// flag is gone), so no chrome routes to /projects/<p>/customer — these entries
// change nothing today. They are kept so any stream-registry routing that
// surfaces a stream path still gets a deterministic label rather than relying on
// prettify() coincidence (#845, §6.5.3).
const SECTION_LABELS: Record<string, string> = {
  operations: "Operations",
  design: "Design",
  hiring: HIRING_DISPLAY_NAME,
  development: "Engineering",
  marketing: "Marketing",
  overview: "Overview",
  // Phase-6 streams (registry mirror):
  customer: "Customer",
  product: "Product",
  money: "Money",
  workforce: "Workforce",
  company: "Company",
};

/** One hop in a breadcrumb trail: a display label + the href that segment maps to. */
export interface Crumb {
  label: string;
  href: string;
}

/**
 * The mode-anchor query param. There is ONE canonical object-detail surface
 * (`/o/<uid>` — Issue 25 pt2, Matt-resolved 2026-07-15); a link that knows its
 * origin appends `?from=<modeSlug>`, and the chrome reads it to stay oriented:
 * the rail lights (`isModeActive` matches `from === mode`), the top-nav crumb
 * resolves the mode (`deriveNavTrail`), and the page renders the context-adaptive
 * `<Mode> › <Kind>s` breadcrumb. {@link objectHref} emits this param. The six
 * former per-mode `/<mode>/o/<uid>` URLs still resolve (they `redirect()` to
 * `/o/<uid>?from=<mode>`), so old bookmarks/deep-links never break.
 */
export const NAV_FROM_PARAM = "from";

/**
 * The five mode slugs — a local literal mirror of `MODE_SLUGS` in
 * `excellent/src/modes/modes-registry.ts` (the five-mode invariant). Kept inline
 * so this pure, React-free module never imports the app's modes layer. Used by
 * {@link objectHref} to decide whether a `from` value is a real mode it can nest
 * the object route under, and by the segment labeler to name a mode-nested path.
 */
const MODE_SEGMENTS: ReadonlySet<string> = new Set([
  "work",
  "customers",
  "product",
  "operations",
  "agents",
]);

/**
 * Build the object-page href — always the ONE canonical object-detail surface
 * (`/o/<uid>`), with the origin mode carried as `?from=<mode>` when known (Issue
 * 25 pt2). The chrome reads `?from` to stay oriented — the rail lights
 * (`isModeActive` matches `from === mode`), the top-nav crumb resolves the mode,
 * and the page renders the context-adaptive `<Mode> › <Kind>s` breadcrumb — so
 * routing straight to the canonical surface (no per-mode redirect hop) preserves
 * context. A mode-less or unrecognized `from` yields a bare `/o/<uid>` (a shared
 * deep-link, a Pulse-wall drill). The single source of the object link shape —
 * every row/card/relationship that opens an object routes through here so the
 * contract can't drift. The legacy `/<mode>/o/<uid>` URLs still redirect here.
 */
export function objectHref(uid: string, from?: string | null): string {
  return from && MODE_SEGMENTS.has(from) ? `/o/${uid}?from=${from}` : `/o/${uid}`;
}

/**
 * The inverse of {@link objectHref}: `/o/<uid>` (with or without `?from=`) →
 * the uid; any other href → null.
 *
 * This is what lets a generic navigator PREFER the object peek without every
 * call site opting in — the `Link` atom and `DataTable`'s `rowHref` both run it
 * and open the peek (`?peek=<uid>`) instead of navigating when it matches.
 * Because `objectHref` is the one producer of this shape, matching it is
 * matching an object link by construction, not sniffing a URL convention.
 * Pure — safe to import from server and client alike.
 */
export function objectUidFromHref(href: string): string | null {
  return /^\/o\/([^/?#]+)/.exec(href)?.[1] ?? null;
}

/**
 * Build a clickable breadcrumb trail from a Next.js pathname — the structured
 * sibling of {@link deriveLabelFromPath} (which joins the same hops into one
 * " · " string). Each crumb's href is the accumulated path up to that segment,
 * so every hop is navigable. Pure — safe to import anywhere.
 *
 * Mirrors deriveLabelFromPath's segment routing (projects tree, section views,
 * product subpaths) and reuses the same label dictionaries. One deliberate
 * divergence: project routes DROP both the "Projects" root AND the project
 * slug — the project-scope switcher sitting beside this breadcrumb in the nav
 * already carries both, so the trail shows only the path beyond it (the
 * section onward).
 */
export function deriveBreadcrumbTrail(pathname: string): Crumb[] {
  if (!pathname || pathname === "/") return [];

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return [];

  const hrefTo = (upto: number) => "/" + segments.slice(0, upto + 1).join("/");
  const crumbs: Crumb[] = [];
  const first = segments[0]!;

  // Project routes show only the path beyond the scope switcher: skip the
  // "Projects" root AND the project slug, leading with the section.
  if (first === "projects") {
    // /projects/sections/<slug> — workspace-wide section view.
    if (segments[1] === "sections" && segments[2]) {
      crumbs.push({
        label: SECTION_LABELS[segments[2]] ?? prettify(segments[2]),
        href: hrefTo(2),
      });
      return crumbs;
    }
    // /projects/<slug>/<section>/<view?>/... — start at the section (index 2).
    if (segments[1] && segments[1] !== "sections" && segments[2]) {
      crumbs.push({
        label: SECTION_LABELS[segments[2]] ?? prettify(segments[2]),
        href: hrefTo(2),
      });
      for (let i = 3; i < segments.length; i++) {
        crumbs.push({ label: prettify(segments[i]!), href: hrefTo(i) });
      }
    }
    return crumbs;
  }

  // Everything else leads with its product/section/mode label. `segmentLabel`
  // names a bare `o` segment "Object" so a mode-nested object route
  // (`/customers/o/<uid>`) reads cleanly here too.
  crumbs.push({ label: PRODUCT_LABELS[first] ?? segmentLabel(first), href: hrefTo(0) });
  if (segments.length === 1) return crumbs;

  // Product sub-routes — first subpath looks up the per-product dictionary.
  const subDict = SUBPATH_LABELS[first];
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i]!;
    const label =
      i === 1 && subDict?.[seg] ? subDict[seg]! : segmentLabel(seg);
    crumbs.push({ label, href: hrefTo(i) });
  }
  return crumbs;
}

export function deriveLabelFromPath(pathname: string): string {
  if (!pathname || pathname === "/") return "Home";

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "Home";

  const first = segments[0]!;
  const productLabel = PRODUCT_LABELS[first] ?? segmentLabel(first);

  if (segments.length === 1) return productLabel;

  // /projects/sections/<slug> — workspace-wide section view.
  if (first === "projects" && segments[1] === "sections" && segments[2]) {
    const sec = SECTION_LABELS[segments[2]] ?? prettify(segments[2]);
    return `${sec} · All projects`;
  }

  // /projects/<slug> — single project landing.
  if (first === "projects" && segments[1] && segments.length === 2) {
    return `${prettifyProjectSlug(segments[1])} · Project`;
  }

  // /projects/<slug>/<section>/<view?>
  if (first === "projects" && segments[1] && segments[2]) {
    const project = prettifyProjectSlug(segments[1]);
    const section = SECTION_LABELS[segments[2]] ?? prettify(segments[2]);
    if (segments[3]) {
      const view = prettify(segments[3]);
      return `${project} · ${section} · ${view}`;
    }
    return `${project} · ${section}`;
  }

  // Product sub-routes with optional id.
  const subDict = SUBPATH_LABELS[first];
  if (subDict && segments[1]) {
    const sub = subDict[segments[1]] ?? prettify(segments[1]);
    if (segments.length === 2) return `${productLabel} · ${sub}`;
    const tail = segments[2]!;
    if (looksLikeId(tail)) {
      return `${productLabel} · ${sub} · ${shortenId(tail)}`;
    }
    if (segments.length === 3) {
      return `${productLabel} · ${sub} · ${prettify(tail)}`;
    }
    return `${productLabel} · ${sub} · ${prettify(tail)} · ${prettify(segments[3]!)}`;
  }

  // Default: join all prettified segments with " · " (a bare `o` reads "Object").
  return [productLabel, ...segments.slice(1).map(segmentLabel)].join(" · ");
}

/**
 * Returns true when a label is generic enough that the server should override
 * it with the derived one. Catches the common case of `document.title` not
 * having been set per-route (everything reads as "Excellent").
 */
export function isGenericLabel(label: string, href: string): boolean {
  if (!label) return true;
  const trimmed = label.trim();
  if (trimmed.length === 0) return true;
  if (trimmed.toLowerCase() === "excellent") return true;
  if (trimmed === href) return true;
  return false;
}

function prettify(seg: string): string {
  if (looksLikeId(seg)) return shortenId(seg);
  return seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Label a single path segment, naming the object-route marker `o` as "Object"
 * (the only route with a bare `o` segment is `/o/<uid>` / `/<mode>/o/<uid>`), else
 * prettifying. Keeps the favorites/recent fallback label clean ("Customers ·
 * Object · 01KVE9K…") instead of the mangled "Customers · O · 01Kve9…".
 */
function segmentLabel(seg: string): string {
  return seg === "o" ? "Object" : prettify(seg);
}

/**
 * Project slugs come from absolute paths (e.g.
 * "-Users-demo-Sites-micro") — strip the leading dash and use only the
 * basename so the rail doesn't show a giant filesystem path.
 */
function prettifyProjectSlug(slug: string): string {
  const cleaned = slug.replace(/^-+/, "");
  const parts = cleaned.split("-");
  const basename = parts[parts.length - 1] || cleaned;
  return prettify(basename);
}

function looksLikeId(s: string): boolean {
  // UUIDs, long hex/hash-like, or pure numbers of 4+ digits.
  if (/^[0-9a-f-]{16,}$/i.test(s)) return true;
  if (/^\d{4,}$/.test(s)) return true;
  // Object ULIDs / uids — 26-char uppercase alphanumeric (the migration
  // `%012X || hex(randomblob(7))` format + ULID Crockford base32). Not caught by
  // the lowercase-hex test (a ULID carries non-hex letters), so name it
  // explicitly → shortened, not mangled, in breadcrumb/favorites labels.
  if (/^[0-9A-Z]{26}$/.test(s)) return true;
  return false;
}

function shortenId(s: string): string {
  if (s.length <= 8) return s;
  return `${s.slice(0, 8)}…`;
}
